/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { inject, Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, of } from 'rxjs'
import { catchError, map, shareReplay } from 'rxjs/operators'
import { LOCALISATION_BASE_URL, LOCALISATION_VERSION } from '@myprofile-ui/myprofile-localisation-api'
import { supportedCountryLanguageCombination } from './resources/pidx-i18n.supported-country-language-combinations'

type LocalizedLanguageNames = Record<string, Record<string, string>>
type SupportedCountryLanguageCombinations = Record<string, { default: string; locales: string[] }>

@Injectable({
  providedIn: 'root',
})
export class PidxI18nService {
  private readonly http = inject(HttpClient)
  private readonly localisationBaseUrl = inject(LOCALISATION_BASE_URL, { optional: true }) ?? 'https://localise.profile.porsche.com'
  private readonly localisationVersion = inject(LOCALISATION_VERSION, { optional: true }) ?? 'LATEST'
  private readonly supportedCountryLanguageCombinations: SupportedCountryLanguageCombinations
  private localizedLanguageNames: LocalizedLanguageNames = {}
  private localizedLanguageNames$!: Observable<LocalizedLanguageNames>

  constructor() {
    this.supportedCountryLanguageCombinations = supportedCountryLanguageCombination
    this.initializeLocalizedLanguageNames()
  }

  private initializeLocalizedLanguageNames(): void {
    const path = `${this.localisationBaseUrl}/localized-language-names/${this.localisationVersion}/localized-language-names.json`

    this.localizedLanguageNames$ = this.http.get<LocalizedLanguageNames>(path).pipe(
      map((response) => {
        this.localizedLanguageNames = response
        return response
      }),
      catchError((error) => {
        console.error('Failed to load localized language names:', error)
        this.localizedLanguageNames = {}
        return of({} as LocalizedLanguageNames)
      }),
      shareReplay(1), // Cache the result
    )

    // Subscribe to initialize the data
    this.localizedLanguageNames$.subscribe()
  }

  /**
   * Get localized language names observable
   */
  getLocalizedLanguageNamesData(): Observable<LocalizedLanguageNames> {
    return this.localizedLanguageNames$
  }

  /**
   * Returns all supported locales for one specified country e.g. `de -> ['de_DE', 'de_AT']`
   */
  getSupportedLocales(country: string): string[] | null {
    country = country.toLowerCase()
    // eslint-disable-next-line no-prototype-builtins
    return this.supportedCountryLanguageCombinations.hasOwnProperty(country)
      ? this.supportedCountryLanguageCombinations[country].locales
      : ['en_GB']
  }

  /**
   * Returns the default locale for the specified country e.g. `de -> 'de_DE'`
   */
  getDefaultLocale(country: string): string | null {
    // eslint-disable-next-line no-prototype-builtins
    return this.supportedCountryLanguageCombinations.hasOwnProperty(country?.toLowerCase())
      ? this.supportedCountryLanguageCombinations[country?.toLowerCase()].default
      : null
  }

  /**
   * Returns whether the supplied country and locale is a valid combination e.g. `de / de_DE -> true`
   */
  isCountryLocaleCombinationSupported(country: string, locale: string): boolean {
    return this.getSupportedLocales(country.toLowerCase())?.some((supportedLocale) => supportedLocale === locale) ?? false
  }

  /**
   * Returns whether the supplied country is supported in our application e.g. `fr -> true`
   */
  isCountrySupported(country: string) {
    // eslint-disable-next-line no-prototype-builtins
    return !!country && this.supportedCountryLanguageCombinations.hasOwnProperty(country.toLowerCase())
  }

  /**
   * Returns an array of locales and the localised language names
   * e.g. -> `fr_FR / ['en_US', 'fr_FR'] -> [{en_US: 'anglais'}, {fr_FR: 'français'}]`
   */
  getLocalizedLanguageName(defaultLocale: string, supportedLocales: string[]): Array<Record<string, string>> {
    const targetLanguage = defaultLocale.split('_')[0]

    const resultArray: Array<Record<string, string>> = []
    supportedLocales.forEach((sLocale) => {
      const country = sLocale.split('_')[0]
      // Safe access with optional chaining and fallback
      const targetLang = this.localizedLanguageNames?.[country]?.[targetLanguage]

      // Only add if we found a translation
      if (targetLang) {
        // We check whether the resultsArray already has an object with the same values. We don't want to have duplicate languages
        if (typeof resultArray.find((obj) => Object.keys(obj).map((key) => obj[key])[0] === targetLang) === 'undefined') {
          resultArray.push({ [sLocale]: targetLang })
        }
      }
    })

    return resultArray
  }

  /**
   * Returns all locales and translated language names source country.
   * If targetLocale is omitted the defaultLocale is used e.g. `ca/fr_FR -> [{en_US: 'anglais'}, {fr_FR: 'français'}]`
   */
  getLanguageChooserLinks(sourceCountry: string, targetLocale?: string): Array<Record<string, string>> {
    const supportedLocales = this.getSupportedLocales(sourceCountry) ?? []
    let tLocale = targetLocale

    if (!tLocale) {
      tLocale = this.getDefaultLocale(sourceCountry) ?? undefined
    }
    return this.getLocalizedLanguageName(tLocale ?? '', supportedLocales)
  }
}
